import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";
import CustomTable from "../../../components/CustomTable";
import { filterList, formatDate } from "../../../libs/utils";
import { useTimezone } from "../../../contexts/TimezoneContext";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import { getAllPartnersList } from "../../../services/partnerConfigurations/partnerConfigurations";
import {
  getRemittanceSummary,
  getPartnerWiseRemittanceSummary,
  getPartnerTradingMetaData,
  getDayWiseRemittanceSummary,
  getFundsDistribution,
  getPartnerBalances,
} from "../../../services/neo/neo";

const PartnersList = () => {
  const { timezoneValue } = useTimezone();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [remittanceData, setRemittanceData] = useState(null);
  const [remittanceLoading, setRemittanceLoading] = useState(false);
  const [partnerSummaryData, setPartnerSummaryData] = useState([]);
  const [partnerSummaryLoading, setPartnerSummaryLoading] = useState(false);
  const [partnerBalances, setPartnerBalances] = useState({});
  const [balanceSummary, setBalanceSummary] = useState({});
  const [partnerBalancesLoading, setPartnerBalancesLoading] = useState(false);
  const [tradingMetaData, setTradingMetaData] = useState(null);
  const [tradingMetaLoading, setTradingMetaLoading] = useState(false);
  const [dayWiseData, setDayWiseData] = useState({});
  const [dayWiseLoading, setDayWiseLoading] = useState(false);
  const [fundsDistributionLoading, setFundsDistributionLoading] =
    useState(false);

  const columns = [
    {
      id: "partnerName",
      label: "Partner Name",
      minWidth: 100,
      enableSort: true,
    },
    {
      id: "active",
      label: "Active",
      minWidth: 100,
      align: "left",
      enableSort: false,
    },
    {
      id: "priority",
      label: "Priority",
      minWidth: 150,
      align: "left",
      enableSort: true,
    },
    {
      id: "limit",
      label: "Partner Limit",
      minWidth: 100,
      align: "left",
      enableSort: true,
    },
    { id: "action", label: "Action", minWidth: 100, align: "left" },
  ];

  const searchData = (e) => {
    setSearchKey(e.target.value);
  };

  const dataGenerator = (data) => {
    return {
      partnerName: data.partner,
      priority: data.priority,
      active: data.active ? "Yes" : "No",
      limit: data.dailyWireLimit,
      action: (
        <Link
          to={`/app/partner-config/${data.partner}`}
          style={{ textDecoration: "none" }}
        >
          <Button variant="contained" size="small" color="warning">
            Edit
          </Button>
        </Link>
      ),
    };
  };

  const fetchPartnersList = async () => {
    setLoading(true);
    const allPartnersListRes = await getAllPartnersList();
    let { success, response: data, error } = allPartnersListRes || {};
    setLoading(false);

    if (success) {
      if (data?.length > 0) {
        setRows(data.map((item) => dataGenerator(item)));
        return;
      }
      return;
    }
    Swal.fire({
      title: "Oops",
      text: error?.message || "Something went wrong",
      icon: "warning",
      confirmButtonText: "Ok",
    });
  };

  const fetchRemittanceSummary = async () => {
    setRemittanceLoading(true);
    const remittanceSummaryRes = await getRemittanceSummary(20);
    setRemittanceLoading(false);

    if (remittanceSummaryRes?.success) {
      setRemittanceData(remittanceSummaryRes.data);
    } else {
      console.error(
        "Error fetching remittance summary:",
        remittanceSummaryRes?.error
      );
      Swal.fire({
        title: "Warning",
        text: "Failed to load remittance summary data",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }
  };

  const fetchPartnerWiseSummary = async () => {
    setPartnerSummaryLoading(true);
    const partnerSummaryRes = await getPartnerWiseRemittanceSummary(20);
    setPartnerSummaryLoading(false);

    if (partnerSummaryRes?.success) {
      setPartnerSummaryData(partnerSummaryRes.data || []);
    } else {
      console.error(
        "Error fetching partner-wise summary:",
        partnerSummaryRes?.error
      );
      Swal.fire({
        title: "Warning",
        text: "Failed to load partner-wise summary data",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }
  };

  const fetchPartnerBalances = async () => {
    setPartnerBalancesLoading(true);
    const balancesRes = await getPartnerBalances();
    setPartnerBalancesLoading(false);

    if (balancesRes?.success) {
      setPartnerBalances(balancesRes.data || {});
      setBalanceSummary(balancesRes.balanceSummary || {});
    } else {
      console.error("Error fetching partner balances:", balancesRes?.error);
    }
  };

  const fetchTradingMetaData = async () => {
    setTradingMetaLoading(true);
    const tradingMetaRes = await getPartnerTradingMetaData();
    setTradingMetaLoading(false);
    const tradingMetaResData = tradingMetaRes.data;

    if (tradingMetaResData?.success) {
      setTradingMetaData(tradingMetaResData.response || {});
    } else {
      console.error("Error fetching trading meta data:", tradingMetaRes?.error);
      Swal.fire({
        title: "Warning",
        text: "Failed to load trading meta data",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }
  };

  const fetchDayWiseSummary = async () => {
    setDayWiseLoading(true);
    const dayWiseRes = await getDayWiseRemittanceSummary();
    setDayWiseLoading(false);

    if (dayWiseRes?.success) {
      setDayWiseData(dayWiseRes.data || {});
    } else {
      console.error("Error fetching day-wise summary:", dayWiseRes?.error);
      Swal.fire({
        title: "Warning",
        text: "Failed to load day-wise summary data",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }
  };

  const fetchFundsDistribution = async () => {
    setFundsDistributionLoading(true);
    const fundsDistributionRes = await getFundsDistribution();
    setFundsDistributionLoading(false);
    if (fundsDistributionRes?.success) {
      Swal.fire({
        title: "Success",
        text: "Funds distributed successfully",
        icon: "success",
        confirmButtonText: "Ok",
      });
    } else {
      Swal.fire({
        title: "Warning",
        text:
          fundsDistributionRes?.error?.message || "Failed to distribute funds",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }
  };

  useEffect(() => {
    fetchPartnersList();
    fetchRemittanceSummary();
    fetchPartnerWiseSummary();
    fetchPartnerBalances();
    fetchTradingMetaData();
    fetchDayWiseSummary();
  }, []);

  return (
    <>
      <Box sx={{ px: 6 }} className="main-box">
        <div className="bankAddRuleHeader mb-3">
          <div
            style={{
              display: "flex",
              padding: "1.5rem 0",
              alignItems: "center",
            }}
          >
            <Typography sx={{ color: "#33343a" }} variant="h4" component="h4">
              Partner Configurations List
            </Typography>
            {loading && (
              <div
                style={{
                  marginLeft: "12px",
                  color: "#a0a0a0",
                  fontSize: "24px",
                }}
              >
                <i
                  className={`fa fa-refresh ${loading === true ? "fa-spin" : ""
                    }`}
                  aria-hidden="true"
                ></i>
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "end" }}>
            <TextField
              id="standard-basic"
              label="Search"
              variant="standard"
              sx={{ mr: 1 }}
              onKeyUp={(e) => searchData(e)}
              InputProps={{
                endAdornment: <SearchIcon sx={{ color: "#8a8a8a" }} />,
              }}
            />
          </div>
        </div>

        <CustomTable
          data={filterList(rows, searchKey) || []}
          columns={columns}
        />
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Overall Summary Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ minHeight: 120 }}>
              <CardContent>
                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                  Partner Not Assigned Summary
                </Typography>
                {remittanceLoading ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className="fa fa-refresh fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>
                    <Typography variant="body2">Loading...</Typography>
                  </div>
                ) : remittanceData ? (
                  <div>
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{ color: "primary.main", fontWeight: "bold" }}
                    >
                      $
                      {remittanceData.total_amount
                        ? parseFloat(
                          remittanceData.total_amount
                        ).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                        : "0.00"}
                    </Typography>
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Partner-wise Summary Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ minHeight: 120 }}>
              <CardContent>
                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                  Assigned Partner-wise Summary
                </Typography>
                {partnerSummaryLoading ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className="fa fa-refresh fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>
                    <Typography variant="body2">Loading...</Typography>
                  </div>
                ) : partnerSummaryData && partnerSummaryData.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "#f5f5f5",
                            borderBottom: "2px solid #ddd",
                          }}
                        >
                          <th
                            style={{
                              padding: "10px",
                              textAlign: "left",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            Partner
                          </th>
                          <th
                            style={{
                              padding: "10px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            Assigned Amount
                          </th>
                          <th
                            style={{
                              padding: "10px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerSummaryData.map((partner, index) => {
                          const balanceInfo = partnerBalances[partner.partner];
                          const balanceAmount =
                            balanceSummary &&
                              Object.prototype.hasOwnProperty.call(
                                balanceSummary,
                                partner.partner
                              )
                              ? balanceSummary[partner.partner]
                              : null;

                          return (
                            <tr key={index} style={{ borderBottom: "1px solid #ddd" }}>
                              <td
                                style={{
                                  padding: "10px",
                                  border: "1px solid #ddd",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Chip
                                  label={partner.partner}
                                  size="small"
                                  color={
                                    partner.partner === "THUNES"
                                      ? "primary"
                                      : partner.partner === "SABER"
                                        ? "secondary"
                                        : partner.partner === "LAYER2"
                                          ? "success"
                                          : partner.partner === "PAYNORMA"
                                            ? "warning"
                                            : "error"
                                  }
                                />
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  border: "1px solid #ddd",
                                  textAlign: "right",
                                  fontWeight: "bold",
                                }}
                              >
                                $
                                {parseFloat(
                                  partner.total_source_amount || 0
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  border: "1px solid #ddd",
                                  textAlign: "right",
                                }}
                              >
                                {partnerBalancesLoading ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontStyle: "italic" }}
                                  >
                                    Loading...
                                  </Typography>
                                ) : balanceAmount !== null ? (
                                  <span>
                                    $
                                    {parseFloat(balanceAmount).toLocaleString(
                                      "en-US",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </span>
                                ) : balanceInfo && !balanceInfo.success ? (
                                  <Typography variant="caption" color="error">
                                    Error
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No partner data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Trading Meta Data Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ minHeight: 120 }}>
              <CardContent>
                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                  Partner Trading Meta Data
                </Typography>
                {tradingMetaLoading ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className="fa fa-refresh fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>
                    <Typography variant="body2">Loading...</Typography>
                  </div>
                ) : tradingMetaData ? (
                  <div>
                    {Object.entries(tradingMetaData).map(
                      ([key, value], index) => (
                        <div key={index} style={{ marginBottom: "8px" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              {key.replace(/_/g, " ")}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontSize: "16px" }}
                            >
                              {typeof value === "number" && value > 0
                                ? `$${value.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                                : value}
                            </Typography>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No trading data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ minHeight: 120 }}>
              <CardContent>
                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                  Funds Distribution
                </Typography>
                {fundsDistributionLoading ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className="fa fa-refresh fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>
                    <Typography variant="body2">Loading...</Typography>
                  </div>
                ) : (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => fetchFundsDistribution()}
                    disabled={fundsDistributionLoading}
                  >
                    Distribute Funds now
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Day-wise Summary Table */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                  Day-wise Remittance Summary
                </Typography>
                {dayWiseLoading ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className="fa fa-refresh fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>
                    <Typography variant="body2">Loading...</Typography>
                  </div>
                ) : dayWiseData && Object.keys(dayWiseData).length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "#f5f5f5",
                            borderBottom: "2px solid #ddd",
                          }}
                        >
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            Date
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#e3f2fd",
                            }}
                          >
                            THUNES
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#f3e5f5",
                            }}
                          >
                            SABER
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#e8f5e8",
                            }}
                          >
                            LAYER2
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#fff3e0",
                            }}
                          >
                            PAYNORMA
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#e1f5fe",
                            }}
                          >
                            NIUM
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#f1f8e9",
                            }}
                          >
                            LEOPAY
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#fce4ec",
                            }}
                          >
                            FINTRACK
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          style={{
                            borderBottom: "2px solid #ddd",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              fontWeight: "bold",
                              textAlign: "left",
                            }}
                          >
                            Total
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#1976d2",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce(
                                (sum, partners) =>
                                  sum + (partners.THUNES || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#7b1fa2",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce(
                                (sum, partners) => sum + (partners.SABER || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#2e7d32",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce(
                                (sum, partners) =>
                                  sum + (partners.LAYER2 || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#f57c00",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce(
                                (sum, partners) =>
                                  sum + (partners.PAYNORMA || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#0277bd",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce(
                                (sum, partners) => sum + (partners.NIUM || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#558b2f",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce(
                                (sum, partners) =>
                                  sum + (partners.LEOPAY || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#c2185b",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce(
                                (sum, partners) =>
                                  sum + (partners.FINTRACK || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              border: "1px solid #ddd",
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "#1976d2",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            $
                            {Object.values(dayWiseData)
                              .reduce((sum, partners) => {
                                const dailyTotal = Object.values(partners).reduce(
                                  (s, amount) => s + (amount || 0),
                                  0
                                );
                                return sum + dailyTotal;
                              }, 0)
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </td>
                        </tr>
                        {Object.entries(dayWiseData).map(
                          ([date, partners], index) => {
                            const dailyTotal = Object.values(partners).reduce(
                              (sum, amount) => sum + amount,
                              0
                            );
                            return (
                              <tr
                                key={index}
                                style={{ borderBottom: "1px solid #ddd" }}
                              >
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    fontWeight: "bold",
                                    color: "#1976d2",
                                  }}
                                >
                                  {formatDate(date, timezoneValue)}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#1976d2",
                                  }}
                                >
                                  $
                                  {partners.THUNES
                                    ? partners.THUNES.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    : "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#7b1fa2",
                                  }}
                                >
                                  $
                                  {partners.SABER
                                    ? partners.SABER.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    : "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#2e7d32",
                                  }}
                                >
                                  $
                                  {partners.LAYER2
                                    ? partners.LAYER2.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    : "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#f57c00",
                                  }}
                                >
                                  $
                                  {partners.PAYNORMA
                                    ? partners.PAYNORMA.toLocaleString(
                                      "en-US",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )
                                    : "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#0277bd",
                                  }}
                                >
                                  $
                                  {partners.NIUM
                                    ? partners.NIUM.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    : "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#558b2f",
                                  }}
                                >
                                  $
                                  {partners.LEOPAY
                                    ? partners.LEOPAY.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    : "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#c2185b",
                                  }}
                                >
                                  $
                                  {partners.FINTRACK
                                    ? partners.FINTRACK.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    : "0.00"}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#1976d2",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  $
                                  {dailyTotal.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No day-wise data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PartnersList;
